import type { ClientPositionView } from "@solomon-apps/shared";
import {
    YaasPositionStatus,
    YaasPositionStatusBadge
} from "@solomon-apps/shared";
import { IndicationUnit } from "@solomon-apps/shared/src/components/Metrics/IndicationUnit";
import {
    Alert,
    AlertDescription
} from "@solomon-apps/shared/src/components/ui/alert";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React from "react";

import { formatLongDate } from "../../shared/format";

/**
 * Human label for the kind of wallet backing a position. Squads multisig
 * positions are sourced from a vault PDA, so the type is worth surfacing;
 * everything else (including legacy rows with no `sourceKind`) is a plain
 * single-signer wallet.
 *
 * Retained from the (now-removed) PositionSummaryCard so the position-detail
 * header can label the source wallet; also exercised by
 * `tests/pages/positionSummaryCard.test.ts`.
 */
export function positionWalletTypeLabel(
    sourceKind: ClientPositionView["sourceKind"]
): string {
    return sourceKind === "multisig-squads"
        ? "Squads multisig"
        : "Standard wallet";
}

/* ── Status block ───────────────────────────────────────── */

export function StatusBlock({
    status,
    rejectionReason,
    rejectedAt,
    suspensionReason,
    suspendedAt
}: {
    status: YaasPositionStatus;
    rejectionReason?: string | null;
    rejectedAt?: string | null;
    suspensionReason?: string | null;
    suspendedAt?: string | null;
}): React.ReactElement {
    if (status === YaasPositionStatus.Active) {
        return <YaasPositionStatusBadge status={status} />;
    }
    if (status === YaasPositionStatus.Rejected) {
        return (
            <div className="flex flex-col gap-1">
                <YaasPositionStatusBadge status={status} />
                {rejectionReason && (
                    <Alert variant="destructive" className="mt-1 max-w-[600px]">
                        <Text variant="small">{rejectionReason}</Text>
                        {rejectedAt && (
                            <AlertDescription>
                                <Text variant="label">
                                    Rejected {formatLongDate(rejectedAt)}
                                </Text>
                            </AlertDescription>
                        )}
                    </Alert>
                )}
            </div>
        );
    }
    if (status === YaasPositionStatus.Suspended) {
        return (
            <div className="flex flex-col gap-1">
                <YaasPositionStatusBadge status={status} />
                {suspensionReason && (
                    <Alert variant="warning" className="mt-1 max-w-[600px]">
                        <Text variant="small">{suspensionReason}</Text>
                        {suspendedAt && (
                            <AlertDescription>
                                <Text variant="label">
                                    Suspended {formatLongDate(suspendedAt)}
                                </Text>
                            </AlertDescription>
                        )}
                    </Alert>
                )}
            </div>
        );
    }
    // Pending
    return (
        <Text
            variant="small"
            render={<span />}
            className="inline-flex items-center gap-1.5 text-foreground-muted"
        >
            <IndicationUnit variant="status" state="pending" size={6} />
            Pending review
        </Text>
    );
}
